/**
 * AYARLAR > GOOGLE BAĞLANTISI — merkezî yönetim: hesap BİR KERE bağlanır, tüm siteler
 * buradan tek tıkla eşlenir. Her ekranda ayrı ayrı "Google'a bağlan" derdi biter:
 * bağlantısı olmayan site zaten sunucu tarafında sessizce devralınır; bu ekran yalnız
 * durumu göstermek ve istisnaları (yanlış property eşleşmesi) elle düzeltmek içindir.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, CircleAlert, RefreshCw, Link2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

type Overview = {
  account: { connected: boolean; email: string | null; authBroken: string | null };
  options: {
    gscSites: { siteUrl: string; permission: string }[];
    gaProperties: { propertyId: string; displayName: string }[];
  };
  sites: { domainId: number; url: string; connected: boolean; gscSiteUrl: string | null; gaPropertyId: string | null }[];
};

const API = "/api";

async function jfetch(path: string, init?: RequestInit): Promise<unknown> {
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body;
}

export function GoogleConnectionsPanel() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [autoBusy, setAutoBusy] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);

  const { data, isLoading } = useQuery<Overview>({
    queryKey: ["google-overview"],
    queryFn: () => jfetch("/me/google-overview") as Promise<Overview>,
    staleTime: 60_000,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ["google-overview"] });

  // OAuth artık tek kullanımlık biletle başlar: önce oturumlu uçtan bilet alınır,
  // sonra Google'a yönlenilir. (Doğrudan connect linki verilseydi, üçüncü biri o linki
  // kurbana gönderip jetonlarını kendi hesabına yazdırabilirdi.)
  const startConnect = async (): Promise<void> => {
    const firstId = data?.sites[0]?.domainId;
    if (!firstId) { toast({ title: "Önce bir site ekleyin", variant: "destructive" }); return; }
    try {
      const out = (await jfetch("/google/connect-ticket", {
        method: "POST",
        body: JSON.stringify({ domainId: firstId, returnTo: "/settings" }),
      })) as { url: string };
      window.location.href = out.url;
    } catch (e) {
      toast({ title: "Bağlantı başlatılamadı", description: (e as Error).message, variant: "destructive" });
    }
  };

  const autoConnect = async (): Promise<void> => {
    setAutoBusy(true);
    try {
      const out = (await jfetch("/me/google-autoconnect", { method: "POST" })) as { results: { ok: boolean; gscSiteUrl: string | null }[] };
      const bagli = out.results.filter((r) => r.ok).length;
      const esli = out.results.filter((r) => r.gscSiteUrl).length;
      toast({ title: "Otomatik eşleme bitti", description: `${bagli}/${out.results.length} site bağlı · ${esli} sitede Search Console eşleşti.` });
      refresh();
    } catch (e) {
      toast({ title: "Eşleme başarısız", description: (e as Error).message, variant: "destructive" });
    } finally {
      setAutoBusy(false);
    }
  };

  const saveSelection = async (domainId: number, patch: { gscSiteUrl?: string; gaPropertyId?: string }): Promise<void> => {
    setSavingId(domainId);
    try {
      await jfetch(`/domains/${domainId}/google-selection`, { method: "PATCH", body: JSON.stringify(patch) });
      refresh();
    } catch (e) {
      toast({ title: "Kaydedilemedi", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSavingId(null);
    }
  };

  if (isLoading) return <Card className="p-6 text-sm text-muted-foreground">Google bağlantı durumu yükleniyor…</Card>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Hesap kartı */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {data.account.connected && !data.account.authBroken ? (
              <CircleCheck className="h-8 w-8 text-emerald-500" />
            ) : (
              <CircleAlert className="h-8 w-8 text-amber-500" />
            )}
            <div>
              <div className="font-semibold">
                {data.account.connected ? `Google hesabı bağlı: ${data.account.email}` : "Google hesabı bağlı değil"}
              </div>
              <div className="text-sm text-muted-foreground">
                {data.account.authBroken
                  ? data.account.authBroken
                  : data.account.connected
                    ? "Search Console + Analytics izinleri hesap düzeyinde alındı — yeni siteler bu bağlantıyı kendiliğinden devralır."
                    : "Bir kez bağlanın; tüm siteleriniz aynı bağlantıyı kullanır, bir daha sorulmaz."}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={data.account.connected && !data.account.authBroken ? "outline" : "default"}
              onClick={() => void startConnect()}
            >
              <Link2 className="mr-2 h-4 w-4" />
              {data.account.connected ? "Bağlantıyı Yenile" : "Google ile Bağlan"}
            </Button>
            {data.account.connected && (
              <Button onClick={() => void autoConnect()} disabled={autoBusy}>
                <Wand2 className="mr-2 h-4 w-4" />
                {autoBusy ? "Eşleniyor…" : "Tüm Siteleri Otomatik Eşle"}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Site eşleme tablosu */}
      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="px-4 py-3 font-medium">Site</th>
              <th className="px-4 py-3 font-medium">Durum</th>
              <th className="px-4 py-3 font-medium">Search Console mülkü</th>
              <th className="px-4 py-3 font-medium">Analytics (GA4) mülkü</th>
            </tr>
          </thead>
          <tbody>
            {data.sites.map((s) => (
              <tr key={s.domainId} className="border-b last:border-0">
                <td className="max-w-[220px] truncate px-4 py-3 font-medium" title={s.url}>
                  {s.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                </td>
                <td className="px-4 py-3">
                  {!s.connected ? (
                    <Badge variant="destructive">Bağlı değil</Badge>
                  ) : data.account.authBroken ? (
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 dark:bg-amber-500/15 dark:text-amber-300">İzin yenilenmeli</Badge>
                  ) : s.gscSiteUrl ? (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-300">Hazır</Badge>
                  ) : (
                    <Badge variant="secondary">Mülk seçilmedi</Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  <select
                    className="w-full max-w-[260px] rounded-lg border border-input bg-background px-2 py-1.5 text-sm outline-none disabled:opacity-50"
                    value={s.gscSiteUrl ?? ""}
                    disabled={!data.account.connected || savingId === s.domainId}
                    onChange={(e) => void saveSelection(s.domainId, { gscSiteUrl: e.target.value })}
                  >
                    <option value="">— seçilmedi —</option>
                    {data.options.gscSites.map((g) => (
                      <option key={g.siteUrl} value={g.siteUrl}>{g.siteUrl}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select
                    className="w-full max-w-[260px] rounded-lg border border-input bg-background px-2 py-1.5 text-sm outline-none disabled:opacity-50"
                    value={s.gaPropertyId ?? ""}
                    disabled={!data.account.connected || savingId === s.domainId}
                    onChange={(e) => void saveSelection(s.domainId, { gaPropertyId: e.target.value })}
                  >
                    <option value="">— seçilmedi —</option>
                    {data.options.gaProperties.map((p) => (
                      <option key={p.propertyId} value={p.propertyId}>{p.displayName} ({p.propertyId})</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
            {data.sites.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Henüz site yok.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <p className="text-xs text-muted-foreground">
        <RefreshCw className="mr-1 inline h-3 w-3" />
        Bağlantısı olmayan bir sitede herhangi bir ekran Google verisi istediğinde, hesabınızın bağlantısı o siteye
        kendiliğinden devralınır ve mülkler alan adına göre otomatik eşlenir — bu tablo yalnız durumu görmek ve
        istisnaları elle düzeltmek içindir.
      </p>
    </div>
  );
}
